import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Users, Phone, TrendingUp, Target } from "lucide-react";
import { getFullName } from "../../services/common/util";
import { getDeskDetailsById } from "../../services/crmServices/deskService";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function DeskDetailModal({ open, onClose, desk }) {
    const [deskDetails, setDeskDetails] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!desk) return;
        getDeskDetailsById(desk._id)
            .then((response) => {
                if (response.status === 200) {
                    setDeskDetails(response.data);
                }
                // Handle the response if needed
            })
            .catch((error) => {
                console.error("Error fetching desk details:", error);
            });
    }, [desk]);

    const handleAgentClick = (agentId) => {
        if (!agentId) {
            console.error("Agent ID is undefined, cannot navigate");
            return;
        }
        navigate(`/agent/${agentId}`);
        onClose(); // Close the modal when navigating to agent page
    };

    if (!deskDetails) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <Users className="h-5 w-5 mr-2" />
                        {deskDetails.name} - Desk Details
                    </DialogTitle>
                    <DialogDescription>
                        Comprehensive overview of desk performance and team assignedAgents
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Desk Manager */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Desk Manager</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    {deskDetails.floorManager ? (
                                        <p className="text-sm text-gray-500">{getFullName(deskDetails.floorManager)}</p>
                                    ) : (
                                        <p className="font-medium">{"Not assigned"}</p>
                                    )}
                                </div>
                                <Badge variant="outline">Floor Manager</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    {deskDetails.manager ? (
                                        <p className="text-sm text-gray-500">{getFullName(deskDetails.manager)}</p>
                                    ) : (
                                        <p className="font-medium">{"Not assigned"}</p>
                                    )}
                                </div>
                                <Badge variant="outline">Manager</Badge>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Performance Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Total Leads</p>
                                        <p className="text-2xl font-bold">{deskDetails.leadCount}</p>
                                    </div>
                                    <Target className="h-8 w-8 text-blue-500" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Calls Today</p>
                                        <p className="text-2xl font-bold">{deskDetails.callTodayCount}</p>
                                    </div>
                                    <Phone className="h-8 w-8 text-green-500" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Conversion Rate</p>
                                        <p className="text-2xl font-bold">{deskDetails.conversionRate}%</p>
                                    </div>
                                    <TrendingUp className="h-8 w-8 text-purple-500" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Team Size</p>
                                        <p className="text-2xl font-bold">{deskDetails.teamSize}</p>
                                    </div>
                                    <Users className="h-8 w-8 text-orange-500" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Team assignedAgents */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Team assignedAgents</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {deskDetails.agents.map((member) => (
                                    <div
                                        key={member._id || member}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-blue-600">
                                                    {`${member.firstName?.charAt(0) || ""}${
                                                        member.lastName ? member.lastName.charAt(0) : ""
                                                    }`}
                                                </span>
                                            </div>
                                            <div>
                                                <Button
                                                    variant="link"
                                                    className="p-0 h-auto text-sm font-medium text-blue-600 hover:text-blue-800"
                                                    onClick={() => handleAgentClick(member._id)}
                                                    disabled={!member._id}
                                                >
                                                    {getFullName(member)}
                                                </Button>
                                                {member.userName && (
                                                    <p className="text-sm text-gray-500">
                                                        @{member.userName}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <div className="text-right">
                                                <p className="text-sm font-medium">
                                                    {member.callCount || 0} calls today
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {member.conversionCount || 0} conversions
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </DialogContent>
        </Dialog>
    );
}
